/**
 * Jain Gaurav Yatra — Members (read-only list).
 * Backend: GET /jain_gaurav_yatra/members/  (cookie-session auth + X-Tenant-ID).
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

function jsonHeaders(): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    "Content-Type": "application/json",
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg = pick(d?.message) ?? pick(d?.detail);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const MEMBERS_PATH = "/jain_gaurav_yatra/members/";

/** Page-facing member shape. */
export interface Member {
  id: string;
  full_name: string | null;
  email: string | null;
  phone: string | null;
  location: string | null;
  created_at: string;
  visited: number;
  total: number;
  isAdmin: boolean;
}

function numOr(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function strOrNull(v: unknown): string | null {
  if (v === null || v === undefined || v === "") {return null;}
  return String(v);
}

function obj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function toMember(row: Record<string, unknown>): Member {
  const progress = obj(row.progress);
  return {
    id: String(row.id ?? ""),
    full_name: strOrNull(row.full_name),
    email: strOrNull(row.email),
    phone: strOrNull(row.phone),
    location: strOrNull(row.location),
    created_at: String(row.created_at ?? ""),
    visited: numOr(progress.visited, 0),
    total: numOr(progress.total, 0),
    isAdmin: row.is_staff === true || String(row.role ?? "").toLowerCase() === "admin",
  };
}

/** Fields accepted when editing a member. */
export interface MemberWrite {
  full_name: string;
  phone: string;
}

function toBody(p: MemberWrite): string {
  return JSON.stringify({
    full_name: p.full_name,
    phone: p.phone,
  });
}

export async function listMembers(): Promise<Member[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${MEMBERS_PATH}`, {
    method: "GET",
    headers: jsonHeaders(),
    credentials: "include",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toMember);
}

/** Fields accepted when creating a member. */
export interface MemberCreate {
  full_name: string;
  email: string;
  phone: string;
  role: string;
  is_staff: boolean;
}

export async function createMember(payload: MemberCreate): Promise<Member> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${MEMBERS_PATH}`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toMember(data as Record<string, unknown>);
}

export async function updateMember(id: string, payload: MemberWrite): Promise<Member> {
  const base = apiBase();
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/`, {
    method: "PATCH",
    headers: jsonHeaders(),
    credentials: "include",
    body: toBody(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toMember(data as Record<string, unknown>);
}

export async function deleteMember(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/`, {
    method: "DELETE",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await res.json().catch(() => ({}));
    throw new Error(errMessage(data, res.status));
  }
}

/** Promote a member to admin: POST /members/:id/make-admin/ */
export async function makeMemberAdmin(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${MEMBERS_PATH}${id}/make-admin/`, {
    method: "POST",
    headers: jsonHeaders(),
    credentials: "include",
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(errMessage(data, res.status));
  }
}
